import * as React from 'react';
import { Overlay } from 'rcd-cpp-ui/ui/loader';
import { View } from '../../../types/View';
import { Metadata } from '../../../types/Metadata';
import './board.scss';
import { ItemCard } from '../ItemCard/itemCard';
import { Pill } from '../../Pill/pill';
import { NUM_PER_PAGE, useActions } from '../viewActions';
import { useActions as useFormActions } from '../../MetadataForm/metadataFormActions';
import InfiniteLoader from '../../InfiniteLoader/infiniteLoader';
import { cn } from '../../../utils';
import { Legend } from '../Legend/legend';
import { Arrow } from 'rcd-cpp-ui/ui/icon/Arrow';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { NodeType } from '../../../enums/NodeType';
import { TriggerLocation } from '../../../enums/TriggerLocation';
import { FieldType } from '../../../enums/FieldType';
import { useSelector } from '../../../utils/useSelector';
import { NewIdeaMenu } from '../../NewIdeaMenu/newIdeaMenu';
import TooltipPopover from '../../Popover/popover';
import { EFSGroupItems } from '../EFSViewComponents/EFSGroupItems';
import { KanbanColumnWidth } from '../../../enums/KanbanColumnWidth';

type Props = { disabled?: boolean; is2DView?: boolean };

const NUM_GROUPS = 10;

export const Board = ({ disabled }: Props) => {
  const selView = useSelector(s => s.view.selected);
  const groups = useSelector(s => s.view.groups);
  const swimlanes = useSelector(s => s.view.swimlanes);
  const columnWidth = useSelector(s => {
    const selected = s.view.selected;
    if (selected?.props?.isEFSBuilder) return KanbanColumnWidth.Expanded;
    const userSettings = s.planning.userSettings;
    const viewSetting = userSettings[selected?.props?.refId];
    return viewSetting?.props?.boardColumnSize;
  });

  const is2DView = useSelector(s => {
    const selected = s.view.selected;
    const userSettings = s.planning.userSettings;
    const viewSetting = userSettings[selected?.props?.refId];
    return (
      selected?.props?.groupBy?.length > 1 && !viewSetting?.props?.hideSwimlanes
    );
  });
  const [numGroups, setNumGroups] = React.useState(NUM_GROUPS);
  const boardRef = React.useRef(null);
  const { reloadData } = useActions();
  const displayGroups = groups?.slice(0, numGroups);
  const maxChar = (displayGroups || []).reduce((l, g) => {
    return Math.max(g?.name?.length, l);
  }, 0);

  const [scrollLeft, setScrollLeft] = React.useState(0);
  React.useEffect(() => {
    const el = boardRef?.current;
    if (!el) return;
    const _handleScroll = () => {
      setScrollLeft(p => {
        if (el.scrollLeft > el.scrollWidth) return p;
        if (p != el.scrollLeft) return el.scrollLeft;
        return p;
      });
    };
    el.addEventListener('scroll', _handleScroll);
    return () => el.removeEventListener('scroll', _handleScroll);
  }, [boardRef]);

  React.useEffect(() => {
    if (selView?.props?.groupBy?.length > 1) {
      if (is2DView && !swimlanes) {
        reloadData();
      } else if (!is2DView && groups?.length) {
        const groupCards = groups.map(g => g.cardRefIds).filter(x => x);
        if (!groupCards.length) reloadData();
      }
    }
  }, [is2DView]);
  const twoLines =
    columnWidth == KanbanColumnWidth.Expanded ? maxChar > 45 : maxChar > 30;
  return (
    <>
      <div
        className={cn('board', `board_${columnWidth}`, {
          'board-2lines': twoLines,
          'board-kanban': !is2DView
        })}
        ref={boardRef}
      >
        <ColumnGrouping groups={displayGroups} />
        <div className="board-headers">
          {displayGroups?.map(g => {
            return (
              <BoardGroup
                key={`${g.labels?.join(':')}_${g.id}`}
                {...g}
                is2DView={is2DView}
                disabled={disabled}
              />
            );
          })}
        </div>
        {is2DView && !swimlanes && <Overlay />}
        {is2DView &&
          swimlanes?.map(g => (
            <Swimlane
              key={g.id}
              left={scrollLeft}
              swimlane={g}
              numGroups={numGroups}
              groups={groups}
              disabled={disabled}
            />
          ))}
        {!!groups?.length && groups.length > numGroups && (
          <InfiniteLoader
            horizontal
            container={boardRef}
            loadData={cb => {
              setNumGroups(numGroups + NUM_GROUPS);
              cb();
            }}
          />
        )}
      </div>
      <Legend />
    </>
  );
};

const Swimlane = ({
  groups,
  swimlane,
  numGroups,
  left
}: Props & {
  swimlane: View.Group;
  groups: any;
  numGroups: number;
  left: number;
}) => {
  const { expanded, subgroups, prevSubGroups } = swimlane;
  const [loading, setLoading] = React.useState(false);
  const { loadSwimlane, toggleSwimlane } = useActions();
  React.useEffect(() => {
    if (typeof expanded == 'boolean' && subgroups === null) {
      if (expanded == true) {
        setLoading(true);
        loadSwimlane(null, swimlane).finally(() => {
          setLoading(false);
        });
      } else {
        toggleSwimlane(swimlane.id, false);
      }
    }
  }, [expanded, subgroups]);
  const _subgroups = subgroups || prevSubGroups;
  return (
    <div className="board-subgroup">
      <div className="board-group-header">
        <div
          style={{ gridRow: 1, marginLeft: left + 'px' }}
          className="board-group-name"
          onClick={() => {
            if (expanded === undefined) {
              setLoading(true);
              loadSwimlane(null, swimlane).finally(() => {
                setLoading(false);
              });
            }
            toggleSwimlane(swimlane.id, !expanded);
          }}
        >
          <Arrow type="solid" direction={!!expanded ? 'down' : 'right'} />
          <div>{swimlane.name}</div>
          <span>{swimlane.count}</span>
        </div>
        {!!expanded && (
          <div className="board-groups board-item-groups board-subgroup-count">
            {groups?.slice(0, numGroups).map(g => {
              const subgroup = findOne(_subgroups, { groupId: g.id });
              if (loading)
                return (
                  <div style={{ position: 'relative' }}>
                    <Overlay spinnerSize="icon" />
                  </div>
                );
              if (subgroup)
                return (
                  <div>
                    <span>{subgroup.count || 0}</span>
                  </div>
                );
              return <div></div>;
            })}
          </div>
        )}
      </div>
      <div className="board-groups board-item-groups">
        {groups?.slice(0, numGroups).map(g => {
          if (!expanded)
            return (
              <div className="board-group-items board-group-items--hidden" />
            );
          return (
            <SwimlaneSubgroup
              key={`${g.labels?.join(':')}_${g.id}`}
              loadingData={loading}
              swimlane={swimlane}
              group={g}
            />
          );
        })}
      </div>
    </div>
  );
};

const BoardGroup = ({ disabled, is2DView, ...group }: View.Group & Props) => {
  const isEFSBuilder = useSelector(s => s.view?.selected?.props.isEFSBuilder);
  const dataSchema = useSelector(s => s.view?.selected?.dataSchema);
  const workspace = useSelector(s => s.planning?.selectedWorkspace);
  const searchHash = useSelector(s => s.view.searchHash);
  const { showForm } = useFormActions();

  const g = { ...group };

  let showAdd =
    !disabled &&
    dataSchema &&
    (!dataSchema.isSystemSchema ||
      dataSchema.props?.allowAddDataWithBaseSchema);
  if (
    dataSchema?.props?.type == NodeType.Item &&
    !workspace?.props?.allowAddNewItems
  ) {
    showAdd = false;
  }
  if (isEFSBuilder) {
    showAdd = false;
  }

  return (
    <div className="board-group">
      <Pill className="board-group-name" name={g.name} title={g.name}>
        <span>{g.count}</span>
      </Pill>
      {showAdd &&
        (dataSchema?.props?.type == NodeType.Item ? (
          <NewIdeaMenu
            triggerLocation={TriggerLocation.BOARD_GROUP}
            position={'left'}
          />
        ) : (
          <TooltipPopover
            type={'info'}
            content={'New Planning Item'}
            packed={true}
          >
            <div
              className="board-group-add"
              onClick={() => {
                let m = {
                  props: {},
                  triggerLocation: TriggerLocation.BOARD_GROUP
                } as Metadata;
                if (g?.props) {
                  const f = dataSchema?.props?.fields?.filter(
                    f =>
                      f.fieldType == FieldType.reference &&
                      g.labels?.includes(f.dataType as NodeType)
                  )[0];
                  if (f) {
                    m.props[f.field] = [g];
                  }
                }
                showForm(m);
              }}
            >
              +
            </div>
          </TooltipPopover>
        ))}
      {isEFSBuilder ? (
        <EFSGroupItems group={group} />
      ) : (
        !is2DView && <GroupItems group={group} key={searchHash} />
      )}
    </div>
  );
};

const GroupItems = ({ group }: { group: View.Group }) => {
  const { loadGroupData } = useActions();
  const [loading, setLoading] = React.useState(false);
  const [limit, setLimit] = React.useState(NUM_PER_PAGE);

  React.useEffect(() => {
    if (limit > NUM_PER_PAGE && loading) {
      loadGroupData(group, limit).finally(() => {
        setLoading(false);
      });
    }
  }, [loading, limit]);

  const scrollContainer = React.useRef(null);
  const cardRefIds = group.cardRefIds;
  if (!cardRefIds) {
    return (
      <div className="board-group-items">
        <Overlay spinnerSize="icon" />
      </div>
    );
  }

  return (
    <div className="board-group-items" ref={scrollContainer}>
      {cardRefIds.map(d => (
        <BoardItem key={d} group={group} refId={d} />
      ))}
      {!!group?.count && cardRefIds.length < group?.count && (
        <InfiniteLoader
          container={scrollContainer}
          loading={loading}
          handleScroll={() => {
            if (!loading) {
              setLimit(limit + NUM_PER_PAGE);
              setLoading(true);
            }
          }}
        />
      )}
    </div>
  );
};

const SwimlaneSubgroup = ({
  group,
  swimlane,
  loadingData
}: {
  group: View.Group;
  swimlane: View.Group;
  loadingData: boolean;
}) => {
  const subgroups = swimlane.subgroups || swimlane.prevSubGroups;
  const subgroup = findOne(subgroups, { groupId: group.id });
  const scrollContainer = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  const [limit, setLimit] = React.useState(NUM_PER_PAGE);
  const cardRefIds = subgroup?.cardRefIds || [];
  const { loadSwimlane } = useActions();
  React.useEffect(() => {
    if (limit != NUM_PER_PAGE) {
      loadSwimlane(group, swimlane, limit).finally(() => setLoading(false));
    }
  }, [loadingData, limit]);
  return (
    <div className="board-group-items" ref={scrollContainer}>
      {cardRefIds.map(d => (
        <BoardItem key={d} group={group} refId={d} />
      ))}
      {!loadingData &&
        !!subgroup?.count &&
        cardRefIds.length < subgroup?.count && (
          <InfiniteLoader
            container={scrollContainer}
            loading={loading}
            handleScroll={() => {
              setLimit(limit + NUM_PER_PAGE);
              setLoading(true);
            }}
          />
        )}
    </div>
  );
};

const BoardItem = ({ group, refId }: { group: View.Group; refId: string }) => {
  const view = useSelector(s => s.view?.selected);
  const item = useSelector(s => s.view.cards[refId]);
  if (!item) return null;
  return (
    <div>
      <ItemCard item={item} group={group} view={view} />
    </div>
  );
};

const ColumnGrouping = ({ groups }: { groups: View.Group[] }) => {
  const columnGroupings = groups?.reduce((groupings, { id, parent }) => {
    if (parent) {
      const p = findOne(groupings, parent);
      if (!!p) {
        p.childIds.push(id);
      } else {
        groupings.push({ ...parent, childIds: [id] });
      }
    }
    return groupings;
  }, [] as { refId: string; name: string; childIds: string[] }[]);
  if (!columnGroupings?.length) return null;

  return (
    <div className="board-column-grouping">
      {columnGroupings.map(cg => (
        <div key={cg.refId}>
          <div className="pill" title={cg.name}>
            {cg.name}
          </div>
          <div>
            {cg.childIds.map(i => (
              <div className="board-group board-group-hidden" key={i}></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
